import { useState, useEffect } from 'react';
import { goalsService, CreateGoalInput } from '../../services/goalsService';
import { Card } from '../ui/Card';
import toast from 'react-hot-toast';
import axios from 'axios';
import {
  TrendingDown,
  Dumbbell,
  Scale,
  Activity,
  Heart,
  Pill,
  Move,
  Thermometer,
  Target,
  FileText,
  X
} from 'lucide-react';

const API_URL = import.meta.env.VITE_API_URL;

interface CreateGoalModalProps {
  onClose: () => void;
  onSuccess: () => void;
  template?: { goalType?: string; title: string; description: string } | null;
}

// Values must match Prisma GoalType enum exactly
const GOAL_TYPES = [
  { value: 'WEIGHT_LOSS', label: 'Slăbire', icon: TrendingDown, description: 'Scădere în greutate', color: 'bg-red-50 text-red-700 border-red-200' },
  { value: 'MUSCLE_GAIN', label: 'Masă Musculară', icon: Dumbbell, description: 'Creștere masă musculară', color: 'bg-blue-50 text-blue-700 border-blue-200' },
  { value: 'MAINTAIN_WEIGHT', label: 'Menținere Greutate', icon: Scale, description: 'Menținere greutate actuală', color: 'bg-gray-50 text-gray-700 border-gray-200' },
  { value: 'START_EXERCISE', label: 'Fitness & Sport', icon: Activity, description: 'Îmbunătățire condiție fizică', color: 'bg-green-50 text-green-700 border-green-200' },
  { value: 'REDUCE_JOINT_PAIN', label: 'Reducere Dureri', icon: Heart, description: 'Reducere dureri articulare', color: 'bg-purple-50 text-purple-700 border-purple-200' },
  { value: 'CONTROL_CHRONIC_PAIN', label: 'Control Durere Cronică', icon: Pill, description: 'Gestionare durere cronică', color: 'bg-pink-50 text-pink-700 border-pink-200' },
  { value: 'REGAIN_MOBILITY', label: 'Mobilitate', icon: Move, description: 'Recâștigare mobilitate', color: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
  { value: 'PAIN_MANAGEMENT', label: 'Management Durere', icon: Thermometer, description: 'Gestionare durere cronică', color: 'bg-orange-50 text-orange-700 border-orange-200' },
];

const GOAL_PLACEHOLDERS: Record<string, {
  title: string;
  current: string;
  target: string;
  unit: string;
}> = {
  WEIGHT_LOSS: {
    title: 'Slăbesc 10kg până la vară',
    current: '80',
    target: '70',
    unit: 'kg'
  },
  MUSCLE_GAIN: {
    title: 'Cresc masa musculară cu 5kg',
    current: '70',
    target: '75',
    unit: 'kg'
  },
  MAINTAIN_WEIGHT: {
    title: 'Mențin greutatea de 75kg',
    current: '75',
    target: '75',
    unit: 'kg'
  },
  START_EXERCISE: {
    title: 'Alerg 5km fără oprire',
    current: '2',
    target: '5',
    unit: 'km'
  },
  REDUCE_JOINT_PAIN: {
    title: 'Reduc durerea articulară la nivel 2',
    current: '7',
    target: '2',
    unit: 'nivel'
  },
  CONTROL_CHRONIC_PAIN: {
    title: 'Controlez durerea cronică la nivel 3',
    current: '8',
    target: '3',
    unit: 'nivel durere'
  },
  REGAIN_MOBILITY: {
    title: 'Merg 10.000 pași zilnic',
    current: '5000',
    target: '10000',
    unit: 'pași'
  },
  PAIN_MANAGEMENT: {
    title: 'Reduc durerea la nivel 3/10',
    current: '7',
    target: '3',
    unit: 'nivel durere'
  }
};

export default function CreateGoalModal({ onClose, onSuccess, template }: CreateGoalModalProps) {
  // If template has goalType, start at step 2, otherwise step 1
  const [step, setStep] = useState(template?.goalType ? 2 : 1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [profileData, setProfileData] = useState<any>(null);

  const [formData, setFormData] = useState<CreateGoalInput>({
    goalType: template?.goalType || '',
    title: template?.title || '',
    description: template?.description || '',
    targetValue: undefined,
    currentValue: undefined,
    unit: '',
    targetDate: undefined,
  });

  // Load medical profile when modal opens
  useEffect(() => {
    const loadProfile = async () => {
      try {
        const token = localStorage.getItem('accessToken');
        if (!token) {
          console.error('[GOALS] No access token found');
          return;
        }
        
        const response = await axios.get(`${API_URL}/api/profile`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        console.log('[GOALS] Profile loaded:', response.data);
        console.log('[GOALS] Weight from profile:', response.data.weight);
        setProfileData(response.data);
      } catch (err: any) {
        console.error('[GOALS] Error loading profile:', err.response?.status, err.message);
      }
    };
    
    loadProfile();
  }, []);

  // Auto-fill current value from profile when goal type is selected
  useEffect(() => {
    console.log('[GOALS] Auto-fill effect triggered:', {
      goalType: formData.goalType,
      hasProfile: !!profileData,
      weight: profileData?.weight,
      currentValue: formData.currentValue
    });

    if (!formData.goalType || !profileData) {
      console.log('[GOALS] Skipping auto-fill: missing goalType or profileData');
      return;
    }

    // Auto-fill based on goal type
    const weightGoals = ['WEIGHT_LOSS', 'MUSCLE_GAIN', 'MAINTAIN_WEIGHT'];
    if (weightGoals.includes(formData.goalType)) {
      console.log('[GOALS] This is a weight goal, checking profile weight...');
      
      if (profileData.weight !== null && profileData.weight !== undefined) {
        console.log('[GOALS] ✅ Auto-filling weight:', profileData.weight);
        setFormData(prev => ({
          ...prev,
          currentValue: profileData.weight,
          unit: 'kg'
        }));
      } else {
        console.log('[GOALS] ❌ No weight in profile');
      }
    }
  }, [formData.goalType, profileData]);

  const getPlaceholders = () => {
    return GOAL_PLACEHOLDERS[formData.goalType] || GOAL_PLACEHOLDERS.WEIGHT_LOSS;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.goalType || !formData.title) {
      toast.error('Te rog completează toate câmpurile obligatorii', {
        duration: 4000,
      });
      return;
    }

    setLoading(true);
    setError('');

    try {
      await goalsService.createGoal(formData);
      toast.success('Obiectiv creat cu succes! 🎯', {
        duration: 3000,
        icon: '✅',
      });
      onSuccess();
      onClose();
    } catch (err: any) {
      console.error('[GOALS] Raw error:', err);
      console.error('[GOALS] Error response:', err.response);
      console.error('[GOALS] Error data:', err.response?.data);
      console.error('[GOALS] Error data type:', typeof err.response?.data);
      
      // Parse validation errors from backend
      let validationErrors = err.response?.data;
      
      // If it's a string, try to parse it as JSON
      if (typeof validationErrors === 'string') {
        console.log('[GOALS] Data is string, attempting to parse...');
        try {
          validationErrors = JSON.parse(validationErrors);
          console.log('[GOALS] Parsed successfully:', validationErrors);
        } catch (e) {
          console.error('[GOALS] Failed to parse JSON:', e);
        }
      }
      
      // Extract errors array - backend returns { error: [Array] }
      let errorsArray = validationErrors?.error || validationErrors?.errors || validationErrors?.validationErrors || validationErrors;
      
      // If errorsArray is a string, try to parse it as JSON
      if (typeof errorsArray === 'string') {
        try {
          errorsArray = JSON.parse(errorsArray);
          console.log('[GOALS] Parsed errorsArray from string:', errorsArray);
        } catch (e) {
          console.error('[GOALS] Failed to parse errorsArray:', e);
        }
      }
      
      // Force convert to real array if it's array-like
      if (errorsArray && !Array.isArray(errorsArray) && typeof errorsArray === 'object' && errorsArray.length !== undefined) {
        errorsArray = Array.from(errorsArray);
      }
      
      // Format validation errors from backend - check both Array.isArray and array-like
      if (errorsArray && (Array.isArray(errorsArray) || (typeof errorsArray === 'object' && errorsArray.length !== undefined))) {
        const friendlyMessages = errorsArray.map((error: any) => {
          const field = error.path?.[0] || 'câmp';
          const fieldNames: Record<string, string> = {
            'title': 'Titlul obiectivului',
            'description': 'Descrierea',
            'goalType': 'Tipul de obiectiv',
            'targetValue': 'Valoarea țintă',
            'currentValue': 'Valoarea curentă',
            'unit': 'Unitatea de măsură',
            'targetDate': 'Deadline-ul'
          };
          
          const friendlyField = fieldNames[field] || field;
          
          if (error.code === 'too_small') {
            if (error.type === 'string') {
              return `${friendlyField} trebuie să aibă minim ${error.minimum} caractere. Ex: "Slăbesc 20kg"`;
            } else {
              return `${friendlyField} trebuie să fie minim ${error.minimum}`;
            }
          } else if (error.code === 'too_big') {
            return `${friendlyField} trebuie să fie maxim ${error.maximum}`;
          } else if (error.code === 'invalid_type' && error.expected === 'string') {
            return `${friendlyField} este obligatoriu`;
          } else if (error.code === 'invalid_enum_value') {
            return `${friendlyField}: Selectează o opțiune validă`;
          } else if (error.message) {
            return `${friendlyField}: ${error.message}`;
          } else {
            return `${friendlyField}: Eroare validare`;
          }
        });
        
        const errorMessage = friendlyMessages.join('\n');
        setError(errorMessage);
        
        // Show first error in toast for quick visibility
        toast.error(friendlyMessages[0] || 'Verifică câmpurile din formular', {
          duration: 5000,
        });
      } else if (validationErrors?.error) {
        // Single error message
        setError(validationErrors.error);
        toast.error(validationErrors.error, {
          duration: 4000,
        });
      } else {
        // Generic error
        const errorMsg = 'Eroare la crearea obiectivului. Verifică toate câmpurile.';
        setError(errorMsg);
        toast.error(errorMsg, {
          duration: 4000,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
      <Card className="max-w-lg w-full my-8">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
            {step === 1 ? (
              <>
                <Target className="w-6 h-6 text-blue-600" />
                Alege Tipul Obiectivului
              </>
            ) : (
              <>
                <FileText className="w-6 h-6 text-blue-600" />
                Detalii Obiectiv
              </>
            )}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3 flex-1">
                <p className="text-sm font-medium text-red-800">Te rugăm să corectezi următoarele:</p>
                <div className="mt-2 text-sm text-red-700 space-y-1">
                  {error.split('\n').filter(err => err.trim()).map((err, idx) => (
                    <p key={idx}>• {err}</p>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {step === 1 ? (
          // Step 1: Select Goal Type
          <div className="space-y-3 max-h-[60vh] overflow-y-auto">
            {GOAL_TYPES.map((type) => {
              const IconComponent = type.icon;
              return (
                <button
                  key={type.value}
                  onClick={() => {
                    setFormData({ ...formData, goalType: type.value });
                    setStep(2);
                  }}
                  className="w-full text-left p-4 border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all group"
                >
                  <div className="flex items-start gap-4">
                    <div className={`p-3 rounded-xl ${type.color} border group-hover:scale-110 transition-transform`}>
                      <IconComponent className="w-6 h-6" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold text-gray-900">{type.label}</h3>
                      <p className="text-sm text-gray-600 mt-0.5">{type.description}</p>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        ) : (
          // Step 2: Goal Details Form
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Titlu Obiectiv <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder={`ex: ${getPlaceholders().title}`}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Descriere (opțional)
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Detalii suplimentare despre obiectiv..."
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Valoare Curentă
                </label>
                <input
                  type="number"
                  step="0.1"
                  value={formData.currentValue || ''}
                  onChange={(e) => setFormData({ ...formData, currentValue: parseFloat(e.target.value) })}
                  placeholder={getPlaceholders().current}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Valoare Țintă
                </label>
                <input
                  type="number"
                  step="0.1"
                  value={formData.targetValue || ''}
                  onChange={(e) => setFormData({ ...formData, targetValue: parseFloat(e.target.value) })}
                  placeholder={getPlaceholders().target}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Unitate Măsură
              </label>
              <input
                type="text"
                value={formData.unit}
                onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                placeholder={getPlaceholders().unit}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Deadline (opțional)
              </label>
              <input
                type="date"
                value={formData.targetDate ? formData.targetDate.split('T')[0] : ''}
                onChange={(e) => {
                  if (e.target.value) {
                    const date = new Date(e.target.value);
                    date.setHours(23, 59, 59, 999);
                    setFormData({ ...formData, targetDate: date.toISOString() });
                  } else {
                    setFormData({ ...formData, targetDate: undefined });
                  }
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={() => setStep(1)}
                className="flex-1 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium"
              >
                ← Înapoi
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 font-medium disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                {loading ? 'Se creează...' : 'Creează Obiectiv'}
              </button>
            </div>
          </form>
        )}
      </Card>
    </div>
  );
}
